import axios, { type AxiosResponse } from 'axios';

import { COMMANDAPI, getUserData, MEDIAMANAGERAPI, QUERYAPI } from '../constants/global';
import {
	type IPuEventChecklistResponse,
	type IRecordPuEventRequest,
	type IRecordPuEventResponse,
	type PuEventsOutcome,
} from '../interface/pu-events';
import { appendLocationFields } from './upload-location';

// Standalone clients for the PollingUnitEvent vertical (Command write +
// Query read through the gateway). NO 401->logout interceptor: a milestone
// capture failure must never sign the agent out — failed captures queue
// locally and retry (same discipline as vesr-api / agent-assignment-api).
const commandClient = axios.create({ baseURL: COMMANDAPI });
const queryClient = axios.create({ baseURL: QUERYAPI });

const attachAuth = (config: any) => {
	const { token, user } = getUserData();
	if (token) config.headers.Authorization = `Bearer ${token}`;
	if (user?.PartyId) config.headers.PartyId = user.PartyId;
	return config;
};

const coerceJson = (response: AxiosResponse) => {
	if (typeof response.data === 'string' && response.data) {
		try {
			response.data = JSON.parse(response.data);
		} catch {
			// Not JSON — leave as-is.
		}
	}
	return response;
};

commandClient.interceptors.request.use(attachAuth);
commandClient.interceptors.response.use(coerceJson);
queryClient.interceptors.request.use(attachAuth);
queryClient.interceptors.response.use(coerceJson);

const EVENTS = '/api/v1/i-elect/PollingUnitEvent';

const toOutcome = <T>(res: AxiosResponse): PuEventsOutcome<T> => {
	const ok = res.status >= 200 && res.status < 300;
	const outcome: PuEventsOutcome<T> = {
		reachable: true,
		httpStatus: res.status,
		body: ok ? (res.data as T) : undefined,
	};
	if (!ok) {
		const message = res.data?.message || res.data?.error?.message;
		if (typeof message === 'string') outcome.errorMessage = message;
		const code = res.data?.code;
		if (typeof code === 'string') outcome.errorCode = code;
	}
	return outcome;
};

/**
 * Record one milestone. `request.referenceId` is the idempotency key: the
 * backend replays the stored event for a known referenceId (merging any new
 * mediaUrls — this is also how photos attach post-capture) and dedupes a
 * second capture of the same (election, PU, eventType) semantically, so
 * retries are always safe.
 */
export const recordPuEvent = async (request: IRecordPuEventRequest): Promise<PuEventsOutcome<IRecordPuEventResponse>> => {
	try {
		const res = await commandClient.post(`${EVENTS}/record-event`, request, {
			validateStatus: () => true,
		});
		return toOutcome<IRecordPuEventResponse>(res);
	} catch {
		return { reachable: false };
	}
};

/**
 * Upload an optional event photo through the media-manager (the canonical
 * upload service for ALL upload features), geo-stamped like result sheets.
 * Returns the absolute media URI or null — the caller must treat null as
 * "no photo", never push an empty string into mediaUrls.
 */
export const uploadEventPhoto = async (
	file: File,
	coords: { latitude: number; longitude: number; accuracy: number } | null,
	onProgress?: (percent: number) => void
): Promise<string | null> => {
	const fd = new FormData();
	fd.append('upload', file);
	if (coords) appendLocationFields(fd, coords);
	try {
		const { token } = getUserData();
		const res = await axios.post(`${MEDIAMANAGERAPI}/api/FileManager/upload-image`, fd, {
			headers: token ? { Authorization: `Bearer ${token}` } : undefined,
			validateStatus: () => true,
			onUploadProgress: (event) => {
				if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
			},
		});
		// Body shape: { message, data: { uri, publicId, … } } — and the service can
		// return 2xx with a null/empty uri, so validate before trusting it.
		const uri: unknown = res.data?.data?.uri;
		if (res.status >= 200 && res.status < 300 && typeof uri === 'string' && /^https?:\/\//i.test(uri.trim())) {
			return uri.trim();
		}
		return null;
	} catch {
		return null;
	}
};

/**
 * Read back the 8-slot checklist for this PU + election. `resultSource` is
 * the enum NAME (string-enum Mongo storage), not the int.
 */
export const getPuEventChecklist = async (
	electionId: number,
	poolingUnitCode: string
): Promise<PuEventsOutcome<IPuEventChecklistResponse>> => {
	try {
		const res = await queryClient.get(`${EVENTS}/get-checklist`, {
			params: { electionId, poolingUnitCode, resultSource: 'Agent' },
			validateStatus: () => true,
		});
		return toOutcome<IPuEventChecklistResponse>(res);
	} catch {
		return { reachable: false };
	}
};
